/**
 * D4F Premium Product Description
 * Advanced scroll animations & interactions
 */

(() => {
  'use strict';

  // Check if root element exists
  const root = document.querySelector('[data-d4f-premium]');
  if (!root) return;

  // Check for IntersectionObserver support
  if (!('IntersectionObserver' in window)) {
    console.warn('IntersectionObserver not supported');
    return;
  }

  // ==========================================
  // Scroll Reveal Animation
  // ==========================================

  const revealElements = root.querySelectorAll('.d4f-reveal, .d4f-reveal-left, .d4f-reveal-right');

  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    },
    {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1
    }
  );

  revealElements.forEach((el) => {
    revealObserver.observe(el);
  });

  // ==========================================
  // Stats Counter Animation
  // ==========================================

  const stats = root.querySelectorAll('.d4f-stat__value');
  
  const animateCounter = (element) => {
    const target = parseInt(element.textContent.replace(/[^0-9]/g, ''), 10);
    if (isNaN(target)) return;

    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        element.textContent = target + (element.textContent.includes('+') ? '+' : '');
        clearInterval(timer);
      } else {
        element.textContent = Math.floor(current) + (element.textContent.includes('+') ? '+' : '');
      }
    }, duration / steps);
  };

  const statsObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const valueEl = entry.target.querySelector('.d4f-stat__value');
          if (valueEl) {
            animateCounter(valueEl);
            statsObserver.unobserve(entry.target);
          }
        }
      });
    },
    { threshold: 0.5 }
  );

  root.querySelectorAll('.d4f-stat').forEach((stat) => {
    statsObserver.observe(stat);
  });

  // ==========================================
  // Parallax Effect for Hero Image
  // ==========================================

  const heroImage = root.querySelector('.d4f-hero-image img');
  
  if (heroImage) {
    let ticking = false;

    const updateParallax = () => {
      const scrolled = window.pageYOffset;
      const heroSection = heroImage.closest('.d4f-hero-image');
      
      if (!heroSection) return;

      const rect = heroSection.getBoundingClientRect();
      const isInView = rect.top < window.innerHeight && rect.bottom > 0;

      if (isInView) {
        const scrollPercent = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const translateY = scrollPercent * 30 - 15; // Subtle parallax
        heroImage.style.transform = `scale(1.1) translateY(${translateY}px)`;
      }

      ticking = false;
    };

    const requestParallax = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    window.addEventListener('scroll', requestParallax, { passive: true });
  }

  // ==========================================
  // Gallery Card 3D Tilt Effect
  // ==========================================

  const galleryCards = root.querySelectorAll('.d4f-gallery-card');

  galleryCards.forEach((card) => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      const rotateX = (y - centerY) / 20;
      const rotateY = (centerX - x) / 20;

      card.style.transform = `
        perspective(1000px)
        rotateX(${rotateX}deg)
        rotateY(${rotateY}deg)
        translateY(-8px)
        scale(1.02)
      `;
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = '';
    });
  });

  // ==========================================
  // Smooth Scroll for Anchor Links
  // ==========================================

  const anchorLinks = root.querySelectorAll('a[href^="#"]');

  anchorLinks.forEach((link) => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });

  // ==========================================
  // Feature Cards Stagger Animation
  // ==========================================

  const features = root.querySelectorAll('.d4f-feature');

  const featureObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }, index * 100);
          featureObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.2 }
  );

  features.forEach((feature) => {
    feature.style.opacity = '0';
    feature.style.transform = 'translateY(20px)';
    feature.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
    featureObserver.observe(feature);
  });

  // ==========================================
  // Image Lazy Loading Enhancement - FIXED
  // ==========================================

  const lazyImages = root.querySelectorAll('.d4f-gallery-card__image img[loading="lazy"]');

  const imageObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          
          // Entferne das loading attribute und setze src explizit
          const imgSrc = img.getAttribute('src');
          if (imgSrc) {
            img.removeAttribute('loading');
            
            // Wenn das Bild noch nicht geladen ist
            if (!img.complete) {
              img.style.opacity = '0';
              img.style.transition = 'opacity 0.4s ease';
              
              img.addEventListener('load', function onLoad() {
                img.style.opacity = '1';
                img.removeEventListener('load', onLoad);
              });
              
              // Force reload falls nötig
              img.src = imgSrc;
            } else {
              // Bild ist bereits geladen
              img.style.opacity = '1';
            }
          }
          
          imageObserver.unobserve(img);
        }
      });
    },
    { 
      rootMargin: '100px', // Früher laden
      threshold: 0.01
    }
  );

  lazyImages.forEach((img) => {
    // Nur beobachten, nicht initial verstecken
    if (!img.complete) {
      img.style.opacity = '0';
      img.style.transition = 'opacity 0.4s ease';
    }
    imageObserver.observe(img);
  });

  // ==========================================
  // Performance Monitor (Dev only)
  // ==========================================

  if (window.location.search.includes('debug=1')) {
    let lastScrollTime = Date.now();
    let frameCount = 0;

    const monitor = () => {
      frameCount++;
      const now = Date.now();
      
      if (now - lastScrollTime > 1000) {
        console.log(`FPS: ${frameCount}`);
        frameCount = 0;
        lastScrollTime = now;
      }

      requestAnimationFrame(monitor);
    };

    monitor();
  }

  // ==========================================
  // Cleanup on page unload
  // ==========================================

  window.addEventListener('beforeunload', () => {
    revealObserver.disconnect();
    statsObserver.disconnect();
    featureObserver.disconnect();
    imageObserver.disconnect();
  });

  console.log('✨ D4F Premium Product Description loaded');
})();